import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "./hooks/useAuth";
import { useApi } from "./hooks/useApi";
import { RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "./utils/cn";
import {
    DndContext, DragOverlay, useSensor, useSensors, PointerSensor,
    closestCorners, rectIntersection, pointerWithin, KeyboardSensor,
    DragEndEvent, DragStartEvent, UniqueIdentifier
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";

// Context Hooks
import { useApp, useEpics, useProjects, useSessions, useFilters, useTheme, useUsers } from "./context/AppContext";

// Utils & Data
import { STORAGE_KEY, SESSIONS_KEY, PROJECTS_KEY, laneOptions } from "./utils/constants";
import { downloadText } from "./utils/scoring";
import { createSession } from "./utils/sessionManager";
import useIsMobile from "./hooks/useIsMobile";
import { useAnalytics } from "./hooks/useAnalytics";

// Components
import { Sidebar, Header, MobileNav } from "./components/layout";
import {
    ListView, KanbanView, RoadmapView, MatrixView, DashboardView, ExecutiveView,
    ProjectListView, LandingPage, SettingsPage, AdminAuditPage
} from "./components/views";
import { HelpPage } from "./components/pages/HelpPage";
import { AddEpicModal, AddProjectModal, DetailDrawer, CompareModal, DeleteConfirmModal, ConfirmModal } from "./components/modals";






import { FilterPanel, HistoryPanel } from "./components/panels";
import { MobileActionFAB } from "./components/ui";
import { EpicCard } from "./components/cards";
import { Epic, Project, Session, User } from "./types";

/**
 * ZPS Epic Prioritizer 3.0 - Strategic Portfolio Intelligence Platform
 */
export default function EpicPrioritizer() {
    const { instance } = useMsal();
    const { user, isAuthenticated, loading: authLoading, isAdmin, canAccessPage, canPerformAction } = useAuth();
    const api = useApi();
    const isMobile = useIsMobile();
    const { trackPageView, trackAction } = useAnalytics();

    // Global State (Context)
    const { state: appState, actions: appActions } = useApp();
    const {
        epics, filteredEpics, selectedId, selectedEpic,
        updateEpic: contextUpdateEpic, addEpic: contextAddEpic, deleteEpic: contextDeleteEpic,
        getPreviousScore, setSelectedId
    } = useEpics();
    const {
        projects, projectsWithStats, selectedProjectIds, selectedProjects,
        addProject: contextAddProject, updateProject: contextUpdateProject, deleteProject: contextDeleteProject,
        setSelectedProjects
    } = useProjects();
    const { sessions } = useSessions();
    const { filters: activeFilters, setFilters: setActiveFilters, activeFilterCount, uniqueOwners } = useFilters();
    const { isDark, setTheme } = useTheme();

    // Local State (UI)
    const [isLoading, setIsLoading] = useState(false);

    const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
    const [isAddEpicOpen, setIsAddEpicOpen] = useState(false);
    const [isAddProjectOpen, setIsAddProjectOpen] = useState(false);
    const [editingProject, setEditingProject] = useState<Project | null>(null);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [projectSearch, setProjectSearch] = useState("");
    const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
    const { users, setUsers } = useUsers();
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Polling Shield: Track last user action time to prevent background updates from overwriting optimistic UI
    const lastActionTime = React.useRef<number>(0);

    // Data Loading (API Sync)
    const loadData = useCallback(async (isBackground = false) => {
        if (authLoading || !isAuthenticated) return;

        // Shield: If a user action occurred recently (last 5s), skip background refresh
        if (isBackground && Date.now() - lastActionTime.current < 5000) {
            return;
        }

        if (!isBackground) setIsLoading(true);
        try {
            // Fetch data based on user permissions - only call APIs user has access to
            const fetchResults = await Promise.allSettled([
                canPerformAction('read', 'project') ? api.getProjects() : Promise.resolve(null),
                api.getEpics(),
                api.getSessions(),
                canPerformAction('read', 'user') ? api.getUsers() : Promise.resolve(null)
            ]);

            // Process results - only set data if request succeeded
            const [projectsResult, epicsResult, sessionsResult, usersResult] = fetchResults;

            // Note: In a real-time scenario, we might want to merge data instead of replacing to avoid wiping local optimistic state.
            // For now, full replacement is used for simplicity, assuming low collision rate.

            if (projectsResult.status === 'fulfilled' && projectsResult.value) {
                appActions.setProjects(projectsResult.value);
            }
            if (epicsResult.status === 'fulfilled' && epicsResult.value) {
                appActions.setEpics(epicsResult.value);
            }
            if (sessionsResult.status === 'fulfilled' && sessionsResult.value) {
                appActions.setSessions(sessionsResult.value);
            }
            if (usersResult.status === 'fulfilled' && usersResult.value) {
                setUsers(usersResult.value);
            }
        } catch (err) {
            console.error('Data loading error:', err);
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [isAuthenticated, authLoading, api, appActions, canAccessPage, canPerformAction, setUsers]);

    // Initial Load
    useEffect(() => {
        loadData(false);
    }, [loadData]);

    // Polling (every 5 seconds)
    useEffect(() => {
        if (!isAuthenticated) return;

        const intervalId = setInterval(() => {
            // Only poll if window is visible to save resources
            if (!document.hidden) {
                loadData(true);
            }
        }, 30000); // Poll every 30 seconds instead of 5s to reduce network traffic and logs

        return () => clearInterval(intervalId);
    }, [isAuthenticated, loadData]);

    // Actions
    const handleLogin = () => instance.loginRedirect({ scopes: ["User.Read"] });

    const handleSyncToCloudClick = () => {
        if (!isAuthenticated) return;
        setSyncConfirmOpen(true);
    };

    const handleConfirmSync = async () => {
        setSyncConfirmOpen(false);
        setIsLoading(true);
        try {
            // 1. Read Local Data
            const localProjectsStr = localStorage.getItem(PROJECTS_KEY);
            const localEpicsStr = localStorage.getItem(STORAGE_KEY);
            const localSessionsStr = localStorage.getItem(SESSIONS_KEY);

            const localProjects: Project[] = localProjectsStr ? JSON.parse(localProjectsStr) : [];
            const localEpics: Epic[] = localEpicsStr ? JSON.parse(localEpicsStr) : [];
            const localSessions: Session[] = localSessionsStr ? JSON.parse(localSessionsStr) : [];

            if (localProjects.length === 0 && localEpics.length === 0) {
                alert("No local data found to migrate.");
                setIsLoading(false);
                return;
            }

            const idMapping: { projects: Record<string, string>, epics: Record<string, string> } = { projects: {}, epics: {} };

            // 2. Sync Projects (Deduplicated)
            // current 'projects' variable holds Cloud data
            for (const proj of localProjects) {
                // Check for existing cloud project with same name
                const existing = projects.find(p => p.name === proj.name);

                if (existing) {
                    console.log(`Skipping duplicate project: ${proj.name}`);
                    idMapping.projects[proj.id] = existing.id;
                } else {
                    const newProject = await api.createProject({
                        name: proj.name,
                        description: proj.description,
                        owner: proj.owner || user?.name || "Admin",
                        status: proj.status,
                        priority: proj.priority,
                    });
                    idMapping.projects[proj.id] = newProject.id;
                }
            }

            // Ensure default/legacy project exists in mapping
            let defaultProjectId = Object.values(idMapping.projects)[0];

            // If no local projects were mapped (e.g. all skipped or none existed), find a fallback in cloud
            if (!defaultProjectId) {
                const legacy = projects.find(p => p.id === 'project-legacy' || p.name === 'Migrated Project' || p.name === 'Legacy Improvements');
                if (legacy) {
                    defaultProjectId = legacy.id;
                } else {
                    // Create if absolutely no projects exist
                    const newLegacy = await api.createProject({
                        name: "Migrated Project",
                        description: "Created during migration",
                        owner: user?.name || "Admin",
                        status: "active",
                        priority: "medium"
                    });
                    defaultProjectId = newLegacy.id;
                }
            }
            // Map legacy ID to default
            idMapping.projects['project-legacy'] = defaultProjectId;

            // 3. Sync Epics (Deduplicated)
            // current 'epics' variable holds Cloud data
            let epicCount = 0;
            let skippedCount = 0;

            for (const epic of localEpics) {
                let targetProjectId = idMapping.projects[epic.projectId];
                if (!targetProjectId) targetProjectId = defaultProjectId;

                // Check for existing epic with same title in the same project
                const existing = epics.find(e => e.title === epic.title && e.projectId === targetProjectId);

                if (existing) {
                    idMapping.epics[epic.id] = existing.id;
                    skippedCount++;
                } else {
                    const newEpic = await api.createEpic({
                        ...epic,
                        id: undefined,
                        projectId: targetProjectId,
                        userId: user?.localAccountId || user?.homeAccountId
                    });
                    idMapping.epics[epic.id] = newEpic.id;
                    epicCount++;
                }
            }

            // 4. Sync Sessions (Deduplicated)
            // current 'sessions' variable holds Cloud data
            for (const session of localSessions) {
                // Simple check by timestamp which is usually unique enough for sessions
                const existing = sessions.find(s => s.timestamp === session.timestamp);

                if (!existing) {
                    await api.createSession({
                        ...session,
                    });
                }
            }

            alert(`Sync complete! Created ${epicCount} new epics (${skippedCount} skipped as duplicates).`);

            // Refresh Cloud State
            const [p, e, s] = await Promise.all([api.getProjects(), api.getEpics(), api.getSessions()]);
            appActions.setProjects(p);
            appActions.setEpics(e);
            appActions.setSessions(s);

        } catch (err: any) { alert("Migration failed: " + err.message); } finally { setIsLoading(false); }
    };

    const handleUpdate = async (id: string, updates: Partial<Epic>) => {
        lastActionTime.current = Date.now(); // Shield on update
        contextUpdateEpic(id, updates); // Optimistic
        if (isAuthenticated) try { await api.updateEpic(id, updates); } catch (e) { console.error(e); }
    };

    const addEpic = async (data: Partial<Epic>) => {
        lastActionTime.current = Date.now(); // Shield on add
        // Generate temp ID for pessimistic UI update if not auth
        const tempId = `epic-${Date.now()}`;
        const newEpic: Partial<Epic> = {
            title: data.title || "New Epic", lane: data.lane || "Unassigned", so: data.so || "", arq: data.arq || "",
            security: data.security || 3, business: data.business || 3, time: data.time || 3, ops: data.ops || 3,
            confidence: data.confidence || 3, effort: data.effort || 2, sgm: data.sgm || false, notes: data.notes || "",
            projectId: data.projectId || (selectedProjectIds.length === 1 ? selectedProjectIds[0] : null) || "project-legacy",
        };

        if (isAuthenticated) {
            try {
                const created = await api.createEpic(newEpic);
                contextAddEpic(created);
                setSelectedId(created.id);
            } catch (e) { alert("Failed to create epic."); }
        } else {
            contextAddEpic({ ...newEpic, id: tempId } as Epic);
            setSelectedId(tempId);
        }
        setIsAddEpicOpen(false);
    };

    const saveProject = async (data: Partial<Project>) => {
        lastActionTime.current = Date.now(); // Shield on save project
        if (isAuthenticated) {
            try {
                if (data.id) {
                    const updated = await api.updateProject(data.id, data);
                    contextUpdateProject(data.id, updated);
                } else {
                    const created = await api.createProject(data);
                    contextAddProject(created);
                    setSelectedProjects([created.id]);
                    appActions.setActiveView('list');
                }
            } catch (e) { alert("Failed to save project."); return; }
        } else {
            if (data.id) {
                contextUpdateProject(data.id, { ...data, updatedAt: new Date().toISOString() });
            } else {
                const newId = `project-${Date.now()}`;
                contextAddProject({ ...data, id: newId, createdAt: new Date().toISOString() } as Project);
                setSelectedProjects([newId]);
                appActions.setActiveView('list');
            }
        }
        setIsAddProjectOpen(false);
        setEditingProject(null);
    };

    const deleteProject = (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const proj = projects.find(p => p.id === id);
        if (proj) setProjectToDelete(proj);
    };

    const confirmDelete = async () => {
        if (!projectToDelete) return;
        lastActionTime.current = Date.now(); // Shield on delete project

        // Find legacy project (or default)
        const legacyProject = projects.find(p => p.id === 'project-legacy' || p.name === 'Legacy Improvements') || projects[0];
        const targetProjectId = legacyProject && legacyProject.id !== projectToDelete.id ? legacyProject.id : 'project-legacy';

        if (isAuthenticated) {
            try {
                // 1. Move epics to legacy
                const projectEpics = epics.filter(e => e.projectId === projectToDelete.id);
                for (const epic of projectEpics) {
                    await api.updateEpic(epic.id, { projectId: targetProjectId });
                }

                // 2. Delete project
                await api.deleteProject(projectToDelete.id);
            } catch (e) { alert("Failed."); return; }
        }

        // Update local context
        // Optimistic Reassignment
        const projectEpics = epics.filter(e => e.projectId === projectToDelete.id);
        projectEpics.forEach(e => contextUpdateEpic(e.id, { projectId: targetProjectId }));
        contextDeleteProject(projectToDelete.id);

        setProjectToDelete(null);
    };


    const removeEpic = async (id: string) => {
        lastActionTime.current = Date.now(); // Shield on delete epic
        contextDeleteEpic(id); // Optimistic
        if (isAuthenticated) try { await api.deleteEpic(id); } catch (e) { console.error(e); }
    };

    const getProjectName = useCallback((id?: string) => projects.find(p => p.id === id)?.name || null, [projects]);

    const exportData = useCallback(() => {
        const data = { exportDate: new Date().toISOString(), total: epics.length, epics: epics.map(e => ({ ...e, wsjf: e.wsjf?.toFixed(2) })) };
        downloadText(`zps-export.json`, JSON.stringify(data, null, 2), 'application/json');
    }, [epics]);

    const handleSaveSession = async () => {
        lastActionTime.current = Date.now(); // Shield on save session
        const label = window.prompt("Enter a label for this session snapshot:");
        if (!label) return;

        const session = createSession(epics, label);

        // Optimistic update
        appActions.saveSession(label); // This updates local state via reducer

        if (isAuthenticated) {
            try {
                await api.createSession(session);
            } catch (e) {
                console.error("Failed to save session to cloud", e);
                alert("Saved locally, but failed to sync to cloud.");
            }
        }
    };

    // DnD - Only enable for users who can update epics (not 'normal' role)
    const canDragAndDrop = canPerformAction('update', 'epic');
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: canDragAndDrop ? 5 : Infinity } // Infinite distance = never activates
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: canDragAndDrop ? sortableKeyboardCoordinates : undefined
        })
    );
    const [activeDragId, setActiveDragId] = useState<UniqueIdentifier | null>(null);
    const customCollisionDetection = (args: any) => {
        const pc = pointerWithin(args); if (pc.length > 0) return pc;
        const rc = rectIntersection(args); if (rc.length > 0) return rc;
        return closestCorners(args);
    };
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragId(null);
        if (!over) return;

        const overId = over.id as string;
        const activeId = active.id as string;

        // Handle List View (Project grouping)
        if (appState.activeView === 'list') {
            // 1. Dropped on a Project Lane
            if (overId === 'legacy-unassigned-fallback' || overId === 'project-legacy' || projects.some(p => p.id === overId)) {
                const targetProjectId = overId === 'legacy-unassigned-fallback' ? 'project-legacy' : overId;
                handleUpdate(activeId, { projectId: targetProjectId });
                return;
            }
            // 2. Dropped on an Epic in a different Project
            const overEpic = epics.find(e => e.id === overId);
            const activeEpic = epics.find(e => e.id === activeId);
            if (overEpic && activeEpic && overEpic.projectId !== activeEpic.projectId) {
                handleUpdate(activeId, { projectId: overEpic.projectId });
            }
            return;
        }

        // Handle Roadmap/Kanban (Lane grouping)
        if ((laneOptions as readonly string[]).includes(overId)) {
            const activeEpic = epics.find(e => e.id === activeId);
            const updates: Partial<Epic> = { lane: overId as any };
            // Auto-complete when dropped on Done lane
            if (overId === 'Done' && activeEpic?.lane !== 'Done') {
                updates.progress = 100;
                updates.completedAt = new Date().toISOString();
            }
            // Reset progress when moving away from Done
            if (overId !== 'Done' && activeEpic?.lane === 'Done') {
                updates.progress = 0;
                updates.completedAt = undefined;
            }
            handleUpdate(activeId, updates);
            return;
        }

        // Dropped on an Epic in a different Lane
        const activeEpic = epics.find(e => e.id === activeId);
        const overEpic = epics.find(e => e.id === overId);
        if (activeEpic && overEpic && activeEpic.lane !== overEpic.lane) {
            const updates: Partial<Epic> = { lane: overEpic.lane };
            // Auto-complete when dropped on Done lane
            if (overEpic.lane === 'Done' && activeEpic.lane !== 'Done') {
                updates.progress = 100;
                updates.completedAt = new Date().toISOString();
            }
            // Reset progress when moving away from Done
            if (overEpic.lane !== 'Done' && activeEpic.lane === 'Done') {
                updates.progress = 0;
                updates.completedAt = undefined;
            }
            handleUpdate(activeId, updates);
        }
    };

    // Helper for Sidebar toggle to match signature
    const setIsSidebarOpen = (val: boolean) => {
        if (val !== appState.isSidebarOpen) appActions.toggleSidebar();
    };

    // Auto-redirect to first allowed view if current view is not accessible
    useEffect(() => {
        if (!user || authLoading) return;

        const allowedViews = ['projects', 'list', 'kanban', 'roadmap', 'matrix', 'dashboard', 'executive', 'admin-users', 'admin-audit'];
        const currentViewAllowed = canAccessPage(appState.activeView);

        if (!currentViewAllowed) {
            // Find first allowed view
            const firstAllowedView = allowedViews.find(v => canAccessPage(v));
            if (firstAllowedView) {
                appActions.setActiveView(firstAllowedView);
            }
        }
    }, [user, authLoading, appState.activeView, canAccessPage, appActions]);

    // Track page views for analytics
    useEffect(() => {
        if (isAuthenticated && appState.activeView) {
            trackPageView(appState.activeView);
        }
    }, [isAuthenticated, appState.activeView, trackPageView]);

    if (authLoading || (isAuthenticated && isLoading)) return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900"><RefreshCw className="w-8 h-8 text-siemens-petrol animate-spin" /></div>;
    if (!isAuthenticated) return <LandingPage onLogin={handleLogin} />;

    return (
        <DndContext sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={(e: DragStartEvent) => setActiveDragId(e.active.id)} onDragEnd={handleDragEnd}>
            <div className={cn("flex h-screen overflow-hidden font-sans select-none", isDark && "dark")}>
                <Sidebar
                    isMobile={isMobile}
                    isOpen={appState.isSidebarOpen}
                    setIsOpen={setIsSidebarOpen}
                    activeView={appState.activeView}
                    setActiveView={appActions.setActiveView}
                    isDarkMode={isDark}
                    setIsDarkMode={(val) => setTheme(val ? 'dark' : 'light')}
                    isAuthenticated={isAuthenticated}
                    isAdmin={isAdmin}
                    onSync={isAdmin ? handleSyncToCloudClick : undefined}
                    canAccessPage={canAccessPage}
                />
                <main className={cn("flex-1 flex flex-col min-w-0 bg-siemens-stone dark:bg-siemens-deep-blue", isMobile && "pb-[64px]")}>
                    <Header
                        isMobile={isMobile} activeView={appState.activeView} setActiveView={appActions.setActiveView} projects={projects}
                        selectedProjectIds={selectedProjectIds} setSelectedProjectIds={setSelectedProjects}
                        isProjectDropdownOpen={isProjectDropdownOpen} setIsProjectDropdownOpen={setIsProjectDropdownOpen}
                        filteredEpicsCount={filteredEpics.length} searchQuery={appState.search} setSearchQuery={appActions.setSearch}
                        onAddEpic={canPerformAction('create', 'epic') ? () => setIsAddEpicOpen(true) : undefined} onFilter={() => setIsFilterOpen(true)} activeFilterCount={activeFilterCount}
                        onHelp={() => setIsHelpOpen(true)} users={users}
                    />
                    <div className="flex-1 overflow-hidden relative">
                        <AnimatePresence mode="wait">
                            <motion.div key={appState.activeView} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }} transition={{ duration: 0.3 }} className="h-full">
                                {appState.activeView === 'projects' && canAccessPage('projects') && (
                                    <ProjectListView
                                        projects={projectsWithStats}
                                        epics={filteredEpics}
                                        selectedProjectId={selectedProjectIds[0] || null} onSelectProject={(id) => { setSelectedProjects(id ? [id] : []); appActions.setActiveView('list'); }}
                                        onAddProject={canPerformAction('create', 'project') ? () => { setEditingProject(null); setIsAddProjectOpen(true); } : undefined}
                                        onEditProject={canPerformAction('update', 'project') ? (id) => {
                                            const p = projectsWithStats.find(proj => proj.id === id);
                                            if (p) {
                                                setEditingProject(p);
                                                setIsAddProjectOpen(true);
                                            }
                                        } : undefined}
                                        onDeleteProject={canPerformAction('delete', 'project') ? deleteProject : undefined}
                                        search={projectSearch} onSearchChange={setProjectSearch}
                                        users={users}
                                    />
                                )}
                                {appState.activeView === 'list' && canAccessPage('list') && <ListView epics={filteredEpics} selectedId={selectedId} onSelect={setSelectedId} getPreviousScore={getPreviousScore} getProjectName={getProjectName} projects={projects} onUpdate={canPerformAction('update', 'epic') ? handleUpdate : undefined} users={users} userRole={user?.role} />}
                                {appState.activeView === 'kanban' && canAccessPage('kanban') && <KanbanView epics={filteredEpics} selectedId={selectedId} onSelect={setSelectedId} getPreviousScore={getPreviousScore} getProjectName={getProjectName} projects={projects} onUpdate={canPerformAction('update', 'epic') ? handleUpdate : undefined} users={users} userRole={user?.role} />}
                                {appState.activeView === 'roadmap' && canAccessPage('roadmap') && <RoadmapView epics={filteredEpics} selectedId={selectedId} onSelect={setSelectedId} getPreviousScore={getPreviousScore} getProjectName={getProjectName} projects={projects} onUpdate={canPerformAction('update', 'epic') ? handleUpdate : undefined} users={users} userRole={user?.role} />}
                                {appState.activeView === 'matrix' && canAccessPage('matrix') && <MatrixView epics={filteredEpics} selectedId={selectedId} onSelect={setSelectedId} getProjectName={getProjectName} users={users} />}
                                {appState.activeView === 'dashboard' && canAccessPage('dashboard') && <DashboardView epics={filteredEpics} sessions={sessions} projects={projects} users={users} />}
                                {appState.activeView === 'executive' && canAccessPage('executive') && (
                                    <ExecutiveView epics={filteredEpics} sessions={sessions} onSelectEpic={setSelectedId} onAddEpic={canPerformAction('create', 'epic') ? () => setIsAddEpicOpen(true) : undefined} onExport={exportData} onViewHistory={() => setIsHistoryOpen(true)} onCompare={() => setIsCompareOpen(true)} onSaveSession={handleSaveSession} onToggleSettings={() => setTheme(isDark ? 'light' : 'dark')} projects={projects} users={users} />
                                )}
                                {appState.activeView === 'admin-users' && canAccessPage('admin-users') && (
                                    <SettingsPage
                                        onAddEpic={canPerformAction('create', 'epic') ? () => setIsAddEpicOpen(true) : undefined}
                                        onExport={exportData}
                                        onViewHistory={() => setIsHistoryOpen(true)}
                                        onCompare={() => setIsCompareOpen(true)}
                                        onSaveSession={handleSaveSession}
                                        onToggleSettings={() => setTheme(isDark ? 'light' : 'dark')}
                                    />
                                )}
                                {appState.activeView === 'admin-audit' && canAccessPage('admin-audit') && (
                                    <AdminAuditPage />
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </main>
                <DetailDrawer epic={selectedEpic || null} isOpen={!!selectedId} onClose={() => setSelectedId(null)} onUpdate={canPerformAction('update', 'epic') ? handleUpdate : undefined} onDelete={canPerformAction('delete', 'epic') ? removeEpic : undefined} projects={projects} sessions={sessions} users={users} userRole={user?.role} />
                <DragOverlay>{activeDragId ? <div style={{ pointerEvents: 'none' }}><EpicCard epic={epics.find(e => e.id === activeDragId)!} dragging /></div> : null}</DragOverlay>
                <MobileNav activeView={appState.activeView} setActiveView={appActions.setActiveView} isMobile={isMobile} canAccessPage={canAccessPage} />
                {isMobile && canPerformAction('create', 'epic') && <MobileActionFAB onAdd={() => setIsAddEpicOpen(true)} onFilter={() => setIsFilterOpen(true)} activeFilterCount={activeFilterCount} />}
                <HelpPage isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} userRole={user?.role} />
                <AddEpicModal isOpen={isAddEpicOpen} onClose={() => setIsAddEpicOpen(false)} onAdd={addEpic} selectedProjectId={selectedProjectIds.length === 1 ? selectedProjectIds[0] : undefined} users={users} />
                <AddProjectModal isOpen={isAddProjectOpen} onClose={() => { setIsAddProjectOpen(false); setEditingProject(null); }} onAdd={saveProject} project={editingProject || undefined} users={users} />
                <FilterPanel isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} filters={activeFilters} setFilters={setActiveFilters} uniqueOwners={uniqueOwners} users={users} projects={projects} />
                <HistoryPanel isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} sessions={sessions} />
                <CompareModal isOpen={isCompareOpen} onClose={() => setIsCompareOpen(false)} currentEpics={epics} sessions={sessions} />
                <DeleteConfirmModal
                    isOpen={!!projectToDelete}
                    onClose={() => setProjectToDelete(null)}
                    onConfirm={confirmDelete}
                    projectName={projectToDelete?.name || ""}
                    epicCount={epics.filter(e => e.projectId === projectToDelete?.id).length}
                />

                <ConfirmModal
                    isOpen={syncConfirmOpen}
                    onClose={() => setSyncConfirmOpen(false)}
                    onConfirm={handleConfirmSync}
                    title="Sync to Cloud?"
                    message="This will merge LOCAL data into the CLOUD. Duplicates will be skipped."
                    variant="info"
                    confirmLabel="Start Sync"
                />
            </div>
        </DndContext>
    );
}
